// apps/web/src/components/visitors/QRCodeScannerDialog.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Close as CloseIcon,
  FlipCameraAndroid as FlipCameraIcon,
  FlashOn as FlashOnIcon,
  FlashOff as FlashOffIcon,
  QrCodeScanner as ScannerIcon,
} from '@mui/icons-material';
import { Html5Qrcode } from 'html5-qrcode';
import { Visitor } from '@gym-management/shared';
import { apiClient } from '../../api/client';
import './QRCodeScannerDialog.css';

const SCANNER_TEXTS = {
  instructions: 'Наведите камеру на QR-код',
  switchCamera: 'Сменить камеру',
  toggleTorch: 'Включить/выключить фонарик',
  scanAgain: 'Сканировать снова',
  processing: 'Обработка QR-кода...',
  fetchingVisitor: 'Получение информации о посетителе',
  errors: {
    invalidFormat: 'Неверный формат QR-кода',
    notVisitorQR: 'Этот QR-код не является кодом посетителя спортзала',
    visitorNotFound: 'Посетитель не найден. Возможно, QR-код устарел или был удалён.',
    networkError: 'Ошибка сети. Проверьте подключение к интернету.',
    cameraNotFound: 'Камера не найдена на устройстве',
    cameraPermission: 'Доступ к камере запрещен. Разрешите доступ в настройках браузера.',
    cameraInit: 'Не удалось инициализировать камеру',
    torchNotSupported: 'Фонарик не поддерживается на этом устройстве',
    unknown: 'Произошла неизвестная ошибка',
    switchFailed: 'Не удалось переключить камеру',
  },
  initializing: 'Инициализация камеры...',
} as const;

interface QRCodeScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onVisitorFound?: (visitor: Visitor) => void;
}

const QRCodeScannerDialog: React.FC<QRCodeScannerDialogProps> = ({
  open,
  onClose,
  onVisitorFound,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');

  const isInitializingRef = useRef(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const qrCodeRegionId = 'qr-reader';
  const isMountedRef = useRef(true);
  const processingRef = useRef(false);
  const lastScannedIdRef = useRef<string | null>(null);

  const cleanupScanner = useCallback(async () => {
    if (scannerRef.current && scannerRef.current.isScanning) {
      try {
        await scannerRef.current.stop();
      } catch (err) {
        console.warn('Error stopping scanner:', err);
      }
    }
    scannerRef.current = null;
    setScanning(false);
    setCameraReady(false);
    setTorchAvailable(false);
    setTorchOn(false);
    lastScannedIdRef.current = null;
  }, []);

  const handleQRCodeScanned = useCallback(
    async (decodedText: string) => {
      if (!isMountedRef.current || processingRef.current) return;

      processingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        let parsedData;
        try {
          parsedData = JSON.parse(decodedText);
        } catch {
          throw new Error(SCANNER_TEXTS.errors.invalidFormat);
        }

        if (parsedData.type !== 'gym_visitor' || !parsedData.id) {
          throw new Error(SCANNER_TEXTS.errors.notVisitorQR);
        }

        if (!/^[0-9a-fA-F]{24}$/.test(parsedData.id)) {
          throw new Error(SCANNER_TEXTS.errors.invalidFormat);
        }

        if (lastScannedIdRef.current === parsedData.id) {
          processingRef.current = false;
          setLoading(false);
          return;
        }

        lastScannedIdRef.current = parsedData.id;

        const visitor = await apiClient.visitors.getVisitor(parsedData.id);
        await cleanupScanner();

        if (isMountedRef.current) {
          if (onVisitorFound) {
            onVisitorFound(visitor);
          }
          onClose();
        }
      } catch (err) {
        const error = err as Error;
        await cleanupScanner();

        if (isMountedRef.current) {
          const errorMessage = error.message || '';
          const lowerCaseMessage = errorMessage.toLowerCase();

          if (
            errorMessage.includes('404') ||
            errorMessage.includes('Not Found') ||
            lowerCaseMessage.includes('not found') ||
            (errorMessage.includes('HTTP error') && errorMessage.includes('404'))
          ) {
            setError(SCANNER_TEXTS.errors.visitorNotFound);
          } else if (
            errorMessage.includes('Network error') ||
            errorMessage.includes('Failed to fetch')
          ) {
            setError(SCANNER_TEXTS.errors.networkError);
          } else {
            setError(errorMessage || SCANNER_TEXTS.errors.unknown);
          }
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
          processingRef.current = false;
        }
      }
    },
    [cleanupScanner, onVisitorFound, onClose]
  );

  const switchCamera = useCallback(async () => {
    if (isInitializingRef.current) return;

    const newMode = facingMode === 'environment' ? 'user' : 'environment';

    try {
      await cleanupScanner();
      await new Promise((resolve) => setTimeout(resolve, 300));
      setFacingMode(newMode);
    } catch {
      setError(SCANNER_TEXTS.errors.switchFailed);
    }
  }, [facingMode, cleanupScanner]);

  const toggleTorch = useCallback(async () => {
    if (!scannerRef.current || !torchAvailable) return;

    try {
      // Используем стандартный тип MediaTrackConstraints, который включает поле advanced
      const constraints: MediaTrackConstraints = {
        advanced: [{ torch: !torchOn }],
      };
      await scannerRef.current.applyVideoConstraints(constraints);
      setTorchOn((prev) => !prev);
    } catch {
      setError(SCANNER_TEXTS.errors.torchNotSupported);
    }
  }, [scannerRef, torchAvailable, torchOn]);

  const initScanner = useCallback(
    async (mode?: 'user' | 'environment') => {
      const targetMode = mode ?? facingMode;
      if (!open || !isMountedRef.current || processingRef.current || isInitializingRef.current) {
        return;
      }
      isInitializingRef.current = true;

      try {
        await cleanupScanner();
        await new Promise((resolve) => setTimeout(resolve, 100));

        const element = document.getElementById(qrCodeRegionId);
        if (!element) {
          setError(SCANNER_TEXTS.errors.cameraInit);
          isInitializingRef.current = false;
          return;
        }

        const scanner = new Html5Qrcode(qrCodeRegionId);
        scannerRef.current = scanner;

        const qrboxSize = isMobile ? 250 : 350;
        const videoConstraints = {
          facingMode: targetMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        };

        await scanner.start(
          { facingMode: targetMode },
          {
            fps: 5,
            qrbox: { width: qrboxSize, height: qrboxSize },
            aspectRatio: 1.0,
            disableFlip: false,
            videoConstraints,
          },
          handleQRCodeScanned,
          () => {}
        );

        try {
          const capabilities = await scanner.getRunningTrackCapabilities();
          if (capabilities && 'torch' in capabilities && capabilities.torch) {
            setTorchAvailable(true);
          }
        } catch {
          setTorchAvailable(false);
        }

        setScanning(true);
        setCameraReady(true);
        setError(null);
      } catch (err: unknown) {
        const error = err as Error;

        if (error.message.includes('already scanning')) {
          return;
        }

        if (
          error.name === 'NotAllowedError' ||
          error.message.includes('permission') ||
          error.message.includes('NotAllowed')
        ) {
          setError(SCANNER_TEXTS.errors.cameraPermission);
        } else if (error.name === 'NotFoundError' || error.message.includes('No camera')) {
          setError(SCANNER_TEXTS.errors.cameraNotFound);
        } else {
          setError(`${SCANNER_TEXTS.errors.cameraInit}: ${error.message}`);
        }
      } finally {
        isInitializingRef.current = false;
      }
    },
    [open, isMobile, cleanupScanner, handleQRCodeScanned, facingMode]
  );

  useEffect(() => {
    if (open) {
      setError(null);
      setLoading(false);
      setScanning(false);
      setCameraReady(false);
      setTorchAvailable(false);
      setTorchOn(false);

      const timer = setTimeout(() => {
        if (isMountedRef.current && open) {
          initScanner();
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [open, initScanner]);

  useEffect(() => {
    if (open && !isInitializingRef.current) {
      const timer = setTimeout(() => {
        initScanner(facingMode);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [facingMode, open, initScanner]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      processingRef.current = false;
      lastScannedIdRef.current = null;
      cleanupScanner();
    };
  }, [cleanupScanner]);

  const handleScanAnother = async () => {
    setError(null);
    setLoading(false);
    processingRef.current = false;
    lastScannedIdRef.current = null;

    try {
      await cleanupScanner();
      setTimeout(() => initScanner(), 100);
    } catch {
      setError('Не удалось перезапустить сканер');
    }
  };

  const handleClose = useCallback(async () => {
    await cleanupScanner();
    setError(null);
    setLoading(false);
    processingRef.current = false;
    lastScannedIdRef.current = null;
    onClose();
  }, [cleanupScanner, onClose]);

  const renderScannerOverlay = () => {
    const qrboxSize = isMobile ? 250 : 350;
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: qrboxSize,
          height: qrboxSize,
          pointerEvents: 'none',
          zIndex: 2,
          boxShadow: '0 0 0 1000px rgba(0, 0, 0, 0.5)',
          '& .corner': {
            position: 'absolute',
            width: 30,
            height: 30,
            borderColor: theme.palette.primary.main,
            borderStyle: 'solid',
          },
          '& .corner-tl': {
            top: 0,
            left: 0,
            borderWidth: '3px 0 0 3px',
          },
          '& .corner-tr': {
            top: 0,
            right: 0,
            borderWidth: '3px 3px 0 0',
          },
          '& .corner-bl': {
            bottom: 0,
            left: 0,
            borderWidth: '0 0 3px 3px',
          },
          '& .corner-br': {
            bottom: 0,
            right: 0,
            borderWidth: '0 3px 3px 0',
          },
        }}
      >
        <Box className="corner corner-tl" />
        <Box className="corner corner-tr" />
        <Box className="corner corner-bl" />
        <Box className="corner corner-br" />
        {scanning && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: 4,
              background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
              animation: 'scan 2s ease-in-out infinite',
              '@keyframes scan': {
                '0%': { top: 0 },
                '50%': { top: 'calc(100% - 4px)' },
                '100%': { top: 0 },
              },
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? false : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          ...(isMobile && { borderRadius: 0 }),
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center">
          <Box sx={{ width: 48 }} />
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
            {SCANNER_TEXTS.instructions}
          </Typography>
          <IconButton onClick={handleClose} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: isMobile ? '60vh' : 'min(600px, 70vh)',
            bgcolor: '#000',
            flexShrink: 0,
          }}
        >
          <div key={facingMode} id={qrCodeRegionId} style={{ width: '100%', height: '100%' }} />

          {!loading && !error && cameraReady && renderScannerOverlay()}

          {!cameraReady && !error && (
            <Fade in={!cameraReady}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={60} sx={{ color: 'white' }} />
                <Typography variant="h6" color="white">
                  {SCANNER_TEXTS.initializing}
                </Typography>
              </Box>
            </Fade>
          )}

          {loading && (
            <Fade in={loading}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={60} sx={{ color: 'white' }} />
                <Typography variant="h6" color="white">
                  {SCANNER_TEXTS.processing}
                </Typography>
                <Typography variant="body2" color="white" sx={{ opacity: 0.7 }}>
                  {SCANNER_TEXTS.fetchingVisitor}
                </Typography>
              </Box>
            </Fade>
          )}

          {error && (
            <Zoom in={!!error}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  p: 3,
                  bgcolor: 'background.paper',
                  zIndex: 10,
                }}
              >
                <Alert severity="error" sx={{ width: '100%' }}>
                  {error}
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleScanAnother}
                  startIcon={<ScannerIcon />}
                  fullWidth={isMobile}
                >
                  {SCANNER_TEXTS.scanAgain}
                </Button>
              </Box>
            </Zoom>
          )}
        </Box>

        {cameraReady && !loading && !error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              p: 2,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <IconButton onClick={switchCamera} color="primary" size="large">
              <FlipCameraIcon />
            </IconButton>
            {torchAvailable && (
              <IconButton onClick={toggleTorch} color="primary" size="large">
                {torchOn ? <FlashOnIcon /> : <FlashOffIcon />}
              </IconButton>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QRCodeScannerDialog;
